import { action, makeObservable, observable } from 'mobx';
import axios, { CancelToken, CancelTokenSource } from 'axios';
import * as React from 'react';

import {
  BaseUploadFileError,
  FileServer,
  FileTags,
  FileType,
  getFileExtensionFromName,
  IFileBase,
  isDataPreviewUrl,
  mapUploadFileErrorToTitle,
  normalizeFile
} from 'shared/entities/file';
import { generateId } from 'shared/entities/common/utils';
import { apiUrls } from 'shared/entities/domain';
import { ValidatorResult } from 'shared/entities/validator';
import { LoadingStage } from 'shared/entities/meta';
import { AppNotificationType } from 'shared/entities/appNotifications';
import { IFilesSelectorStore } from 'shared/entities/store/filesSelectorStore';
import { IRootStore } from 'shared/entities/store/rootStore';
import TypedTransComponent from 'shared/components/TypedTransComponent';

import FileBaseModel from './FileBaseModel';
import { loadFilePreview } from './mediaPreview';
import SmoothedUploadProgress from './SmoothedUploadProgress';
import { getLoadedPercentage } from './utils';

export default class FileModel extends FileBaseModel {
  saved: boolean;
  removingStage: LoadingStage = LoadingStage.NOT_STARTED;

  progress: number;
  error: ValidatorResult;
  filesSelectorStore: IFilesSelectorStore;
  asDocument = false;
  availability: FileTags;
  extra: any;

  previewGenerationDone = true;
  /** Серверный preview_url после аплоада; UI может продолжать показывать локальный data URL */
  serverPreview: string | null = null;

  cancelTokenSource: CancelTokenSource | null = null;

  private readonly silentUploadNotifications: boolean;

  private readonly uploadProgressSmooth = new SmoothedUploadProgress(
    () => this.progress,
    (value) => this.changeProgress(value)
  );

  minTitleLength: number;

  constructor({
    id,
    date,
    fileType,
    size,
    title,
    saved,
    progress,
    preview,
    error,
    url,
    extension,
    filesSelectorStore,
    availability,
    extra,
    rootStore,
    areaSize,
    duration,
    silentUploadNotifications = false,
    previewGenerationDone = true,
    serverPreview = null
  }: IFileBase & {
    saved: boolean;
    progress: number;
    error: ValidatorResult;
    filesSelectorStore: IFilesSelectorStore;
    availability: FileTags;
    rootStore: IRootStore;
    silentUploadNotifications?: boolean;
    previewGenerationDone?: boolean;
    serverPreview?: string | null;
  }) {
    super(
      {
        id,
        size,
        extension,
        title,
        fileType,
        url,
        date,
        preview,
        extra,
        areaSize,
        duration
      },
      rootStore
    );
    this.saved = saved;
    this.progress = progress;
    this.error = error;
    this.filesSelectorStore = filesSelectorStore;
    this.availability = availability;
    this.extra = extra;
    this.minTitleLength = title.length;
    this.silentUploadNotifications = silentUploadNotifications;
    this.previewGenerationDone = previewGenerationDone;
    this.serverPreview = serverPreview;

    makeObservable(this, {
      saved: observable,
      progress: observable,
      error: observable,
      filesSelectorStore: observable,
      removingStage: observable,
      availability: observable,
      minTitleLength: observable,
      previewGenerationDone: observable,
      serverPreview: observable,

      changePreview: action,
      updateParams: action,
      loadPreview: action,
      changeProgress: action,
      setMinTitleLength: action,
      clearProgressTicker: action
    });
  }

  setMinTitleLength = (value: number): void => {
    this.minTitleLength = value;
  };

  changePreview = (value: string | null): void => {
    this.preview = value;
  };

  changeProgress = (value: number): void => {
    this.progress = value;
  };

  clearProgressTicker = (): void => {
    this.uploadProgressSmooth.dispose();
  };

  updateParams = (raw: FileServer) => {
    const {
      id,
      extension,
      url,
      preview,
      fileType,
      date,
      title,
      availability,
      size,
      extra,
      areaSize,
      duration
    } = normalizeFile(raw);

    const prevId = this.id;

    this.clearProgressTicker();

    this.id = id;
    this.availability = availability;
    this.extension = extension;
    this.url = url;
    this.serverPreview = preview;
    const hasLocalDataPreview = isDataPreviewUrl(this.preview);
    this.preview = hasLocalDataPreview
      ? this.preview
      : (preview ?? this.preview);
    this.fileType = fileType;
    this.date = date;
    this.title = title;
    this.size = size;
    this.saved = true;
    this.progress = 100;
    this.extra = extra;
    this.areaSize = areaSize;
    this.duration = duration;

    // меняем заранее рандомно сгенеренный на реальный
    this.filesSelectorStore.changeLoadedFileId({ prevId, newId: this.id });
  };

  loadPreview = (file: File): void => {
    this.previewGenerationDone = false;
    void this.applyFilePreview(file);
  };

  private applyFilePreview = async (file: File): Promise<void> => {
    try {
      const preview = await loadFilePreview(file, this.fileType);

      if (preview !== null) {
        this.changePreview(preview);
      }
    } finally {
      this.previewGenerationDone = true;
    }
  };

  cancelUpload(options?: { silent?: boolean }) {
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel();
      this.clearProgressTicker();

      if (!options?.silent) {
        this.filesSelectorStore.rootStore.appNotificationsStore.open({
          title: (
            <TypedTransComponent
              ns="models"
              i18nKey="file.FileModel.notifications.downloadingFileCancelled"
            >
              Загрузка файла <b>{{ fileName: this.title }}</b> отменена
            </TypedTransComponent>
          ),
          type: AppNotificationType.error
        });
      }

      this.cancelTokenSource = null;
    }
  }

  async upload(file: File): Promise<BaseResponse> {
    this.cancelUpload();

    this.uploadProgressSmooth.start();

    this.cancelTokenSource = axios.CancelToken.source();

    let response: Awaited<
      ReturnType<
        typeof this.rootStore.networkStore.api<FileServer, BaseUploadFileError>
      >
    >;

    try {
      response = await this.rootStore.networkStore.api<
        FileServer,
        BaseUploadFileError
      >(apiUrls.ATTACHMENTS_UPLOAD, {
        method: 'POST',
        data: {
          file
        },
        config: {
          onUploadProgress: (progressEvent) => {
            this.uploadProgressSmooth.reportNetworkProgress(
              getLoadedPercentage(file, progressEvent)
            );
          },
          cancelToken: this.cancelTokenSource.token as CancelToken
        },
        errorsMap: mapUploadFileErrorToTitle,
        multipartFormData: true
      });
    } finally {
      this.cancelTokenSource = null;
      this.clearProgressTicker();
    }

    if (!response.isError) {
      this.updateParams(response.data);

      if (!this.silentUploadNotifications) {
        this.filesSelectorStore.rootStore.appNotificationsStore.open({
          title: (
            <TypedTransComponent
              ns="models"
              i18nKey="file.FileModel.notifications.fileSuccessfullyUploaded"
            >
              Файл <b>{{ fileName: file.name }}</b> успешно загружен
            </TypedTransComponent>
          ),
          type: AppNotificationType.success
        });
      }
    } else {
      this.error = (t) =>
        t('file.FileModel.notifications.downloadError', {
          ns: 'models'
        });
    }

    return {
      isError: response.isError
    };
  }

  static fromJson(
    raw: FileServer,
    filesStore: IFilesSelectorStore,
    rootStore: IRootStore
  ): FileModel {
    const normalized = normalizeFile(raw);

    return new FileModel({
      ...normalized,
      saved: true,
      progress: 100,
      error: null,
      filesSelectorStore: filesStore,
      rootStore,
      serverPreview: normalized.preview
    });
  }

  static fromInitialParams({
    file,
    fileType,
    filesSelectorStore,
    extra,
    availability,
    rootStore,
    silentUploadNotifications
  }: {
    file: File;
    fileType: FileType;
    filesSelectorStore: IFilesSelectorStore;
    availability: FileTags;
    extra: any;
    rootStore: IRootStore;
    silentUploadNotifications?: boolean;
  }): FileModel {
    const model = new FileModel({
      id: generateId(),
      title: file.name,
      size: file.size,
      date: new Date(),
      saved: false,
      fileType,
      progress: 0,
      preview: null,
      error: null,
      url: '',
      extension: getFileExtensionFromName(file.name) || '',
      filesSelectorStore,
      availability,
      extra,
      rootStore,
      areaSize: null,
      duration: null,
      silentUploadNotifications,
      previewGenerationDone: false
    });

    model.loadPreview(file);
    model.upload(file);

    return model;
  }
}
